import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar, 
  Clock, 
  Phone, 
  Mail, 
  MapPin, 
  Briefcase,
  DollarSign,
  FileText,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function ConsultationPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [options, setOptions] = useState({
    project_types: [],
    budget_ranges: [],
    timelines: []
  });
  const [availability, setAvailability] = useState({ slots: [], blocked_dates: [] });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    email: "",
    project_address: "",
    project_type: "",
    budget_range: "",
    timeline: "",
    description: "",
    date: "",
    time: ""
  });

  useEffect(() => {
    fetchOptions();
    fetchAvailability();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await axios.get(`${API}/consultations/options`);
      setOptions(response.data);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/consultations/availability`);
      setAvailability(response.data);
    } catch (error) {
      console.error("Error fetching availability:", error);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/consultations/available-slots?date=${date}`);
      setAvailableSlots(response.data.available_slots || []);
    } catch (error) {
      console.error("Error fetching slots:", error);
      setAvailableSlots([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    setFormData({ ...formData, date: dateStr, time: "" });
    fetchAvailableSlots(dateStr);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/consultations/book`, formData);
      setIsSuccess(true);
      toast.success("Consultation booked successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book consultation");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDateAvailable = (date) => {
    const dayOfWeek = date.getDay();
    // Convert to Monday=0 format
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    const dateStr = date.toISOString().split('T')[0];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if in the past
    if (date < today) return false;
    
    // Check if blocked
    if (availability.blocked_dates.includes(dateStr)) return false;
    
    // Check if day has availability
    const hasSlot = availability.slots.some(s => s.day_of_week === adjustedDay && s.is_active);
    return hasSlot;
  };

  const generateCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];
    
    // Add empty cells for days before the first
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const nextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    const today = new Date();
    const prev = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1);
    if (prev >= new Date(today.getFullYear(), today.getMonth(), 1)) {
      setCalendarMonth(prev);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-24" data-testid="consultation-success">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-[#121212] border border-[#2A2A2A] p-12 text-center">
            <div className="w-20 h-20 bg-[#7ED4C6]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={48} className="text-[#7ED4C6]" />
            </div>
            <h1 className="font-['Oswald'] text-3xl text-white mb-4">
              Consultation Scheduled!
            </h1>
            <p className="text-[#A1A1AA] mb-6">
              Your 30-minute consultation has been booked for:
            </p>
            <div className="bg-[#1E1E1E] p-6 mb-8">
              <p className="text-[#7ED4C6] font-['Oswald'] text-xl">
                {new Date(formData.date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
              <p className="text-white text-2xl font-bold">{formData.time}</p>
            </div>
            <p className="text-[#A1A1AA] mb-8">
              One of our project managers will call you at <strong className="text-white">{formData.phone}</strong> at the scheduled time.
              A confirmation email has been sent to <strong className="text-white">{formData.email}</strong>.
            </p>
            <Link to="/" className="btn-primary inline-flex items-center gap-2">
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="consultation-page">
      {/* Hero */}
      <section className="relative py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
              Free Consultation
            </span>
            <h1 className="font-['Oswald'] text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Schedule Your <span className="text-[#7ED4C6]">30-Minute Call</span>
            </h1>
            <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
              Speak directly with a project manager to discuss your construction needs,
              timeline, and budget. No obligation, just expert guidance.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-12 pb-24">
        <div className="max-w-5xl mx-auto px-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-10 h-10 flex items-center justify-center font-['Oswald'] text-lg ${
                    step >= s
                      ? "bg-[#7ED4C6] text-[#0A0A0A]"
                      : "bg-[#2A2A2A] text-[#A1A1AA]"
                  }`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div
                    className={`w-20 h-1 ${
                      step > s ? "bg-[#7ED4C6]" : "bg-[#2A2A2A]"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Step 1: Project Details */}
            {step === 1 && (
              <div className="bg-[#121212] border border-[#2A2A2A] p-8" data-testid="step-1">
                <h2 className="font-['Oswald'] text-2xl text-white mb-6">
                  Tell Us About Your Project
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white text-sm mb-2">
                      <Briefcase size={14} className="inline mr-2 text-[#7ED4C6]" />
                      Project Type *
                    </label>
                    <select
                      name="project_type"
                      value={formData.project_type}
                      onChange={handleChange}
                      required
                      className="form-input w-full"
                      data-testid="project-type"
                    >
                      <option value="">Select project type</option>
                      {options.project_types.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-white text-sm mb-2">
                      <DollarSign size={14} className="inline mr-2 text-[#7ED4C6]" />
                      Budget Range *
                    </label>
                    <select
                      name="budget_range"
                      value={formData.budget_range}
                      onChange={handleChange}
                      required
                      className="form-input w-full"
                      data-testid="budget-range"
                    >
                      <option value="">Select budget range</option>
                      {options.budget_ranges.map((range) => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-white text-sm mb-2">
                      <Clock size={14} className="inline mr-2 text-[#7ED4C6]" />
                      Project Timeline *
                    </label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      required
                      className="form-input w-full"
                      data-testid="timeline"
                    >
                      <option value="">When do you want to start?</option>
                      {options.timelines.map((tl) => (
                        <option key={tl} value={tl}>{tl}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-white text-sm mb-2">
                      <MapPin size={14} className="inline mr-2 text-[#7ED4C6]" />
                      Project Address/City *
                    </label>
                    <input
                      type="text"
                      name="project_address"
                      value={formData.project_address}
                      onChange={handleChange}
                      required
                      className="form-input w-full"
                      placeholder="e.g., Newark, NJ or 123 Main St"
                      data-testid="project-address"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-white text-sm mb-2">
                      <FileText size={14} className="inline mr-2 text-[#7ED4C6]" />
                      Project Description *
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="form-input w-full resize-none"
                      placeholder="Describe your project goals, requirements, and any specific details..."
                      data-testid="description"
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-8">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    disabled={!formData.project_type || !formData.budget_range || !formData.timeline || !formData.project_address || !formData.description}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                    data-testid="next-step-1"
                  >
                    Continue
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Select Date & Time */}
            {step === 2 && (
              <div className="bg-[#121212] border border-[#2A2A2A] p-8" data-testid="step-2">
                <h2 className="font-['Oswald'] text-2xl text-white mb-6">
                  Choose a Date & Time
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Calendar */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <button
                        type="button"
                        onClick={prevMonth}
                        className="p-2 text-[#A1A1AA] hover:text-white"
                      >
                        <ArrowLeft size={20} />
                      </button>
                      <h3 className="font-['Oswald'] text-lg text-white">
                        {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </h3>
                      <button
                        type="button"
                        onClick={nextMonth}
                        className="p-2 text-[#A1A1AA] hover:text-white"
                      >
                        <ArrowRight size={20} />
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                        <div key={day} className="text-center text-[#A1A1AA] text-xs py-2">
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {generateCalendarDays().map((date, i) => (
                        <div key={i}>
                          {date ? (
                            <button
                              type="button"
                              onClick={() => isDateAvailable(date) && handleDateSelect(date)}
                              disabled={!isDateAvailable(date)}
                              className={`w-full aspect-square flex items-center justify-center text-sm transition-colors ${
                                selectedDate === date.toISOString().split('T')[0]
                                  ? "bg-[#7ED4C6] text-[#0A0A0A] font-bold"
                                  : isDateAvailable(date)
                                  ? "bg-[#1E1E1E] text-white hover:bg-[#2A2A2A]"
                                  : "bg-transparent text-[#3A3A3A] cursor-not-allowed"
                              }`}
                            >
                              {date.getDate()}
                            </button>
                          ) : (
                            <div />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Time Slots */}
                  <div>
                    <h3 className="font-['Oswald'] text-lg text-white mb-4">
                      {selectedDate 
                        ? `Available Times for ${new Date(selectedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                        : "Select a date to see available times"
                      }
                    </h3>
                    {isLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 size={32} className="text-[#7ED4C6] animate-spin" />
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => setFormData({ ...formData, time: slot })}
                            className={`p-3 text-sm transition-colors ${
                              formData.time === slot
                                ? "bg-[#7ED4C6] text-[#0A0A0A] font-bold"
                                : "bg-[#1E1E1E] text-white hover:bg-[#2A2A2A]"
                            }`}
                            data-testid={`time-slot-${slot}`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    ) : selectedDate ? (
                      <p className="text-[#A1A1AA] text-center py-8">
                        No available slots for this date. Please select another date.
                      </p>
                    ) : (
                      <p className="text-[#A1A1AA] text-center py-8">
                        Click on an available date to see time slots.
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex justify-between mt-8">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <ArrowLeft size={18} />
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    disabled={!formData.date || !formData.time}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                    data-testid="next-step-2"
                  >
                    Continue
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Contact Information */}
            {step === 3 && (
              <div className="bg-[#121212] border border-[#2A2A2A] p-8" data-testid="step-3">
                <h2 className="font-['Oswald'] text-2xl text-white mb-6">
                  Your Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-white text-sm mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="form-input w-full"
                      placeholder="John Doe"
                      data-testid="full-name"
                    />
                  </div>
                  <div>
                    <label className="block text-white text-sm mb-2">
                      <Phone size={14} className="inline mr-2 text-[#7ED4C6]" />
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="form-input w-full"
                      placeholder="(123) 456-7890"
                      data-testid="phone"
                    />
                  </div>
                  <div>
                    <label className="block text-white text-sm mb-2">
                      <Mail size={14} className="inline mr-2 text-[#7ED4C6]" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input w-full"
                      placeholder="you@example.com"
                      data-testid="email"
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className="mt-8 p-6 bg-[#1E1E1E] border-l-4 border-[#7ED4C6]">
                  <h3 className="font-['Oswald'] text-lg text-white mb-4">Consultation Summary</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-[#A1A1AA]">Date:</span>
                      <p className="text-white">
                        {formData.date && new Date(formData.date).toLocaleDateString('en-US', { 
                          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
                        })}
                      </p>
                    </div>
                    <div>
                      <span className="text-[#A1A1AA]">Time:</span>
                      <p className="text-white">{formData.time} (30 minutes)</p>
                    </div>
                    <div>
                      <span className="text-[#A1A1AA]">Project Type:</span>
                      <p className="text-white">{formData.project_type}</p>
                    </div>
                    <div>
                      <span className="text-[#A1A1AA]">Budget:</span>
                      <p className="text-white">{formData.budget_range}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-8">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <ArrowLeft size={18} />
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.full_name || !formData.phone || !formData.email}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                    data-testid="submit-booking"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Booking...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Book Consultation
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Why Schedule Section */}
      <section className="py-16 bg-[#121212] border-t border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-['Oswald'] text-2xl text-white text-center mb-12">
            Why Schedule a Consultation?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: FileText, title: "Understand Scope", desc: "We'll discuss your project requirements in detail" },
              { icon: DollarSign, title: "Accurate Pricing", desc: "Get a realistic budget estimate for your project" },
              { icon: Clock, title: "Timeline Planning", desc: "Establish a realistic schedule that works for you" },
              { icon: Phone, title: "Expert Advice", desc: "Receive professional guidance from experienced builders" }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-[#7ED4C6]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon size={32} className="text-[#7ED4C6]" />
                </div>
                <h3 className="font-['Oswald'] text-lg text-white mb-2">{item.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
